import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MessageCircle, Minus, X, ArrowLeft, Send, Check, CheckCheck, ChevronUp } from "lucide-react";

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [mini, setMini] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [active, setActive] = useState(null); // {id,name}
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [disabled, setDisabled] = useState(false);
  const bodyRef = useRef(null);
  const containerRef = useRef(null);
  const poll = useCallback(() => {
    api.get("/chat/poll", { silent: true })
      .then((r) => { setUsers(r.data.users || []); setTotalUnread(r.data.total_unread || 0); setOnlineCount(r.data.online || 0); setDisabled(false); })
      .catch((e) => { if (e.response?.status === 401) setDisabled(true); });
  }, []);

  const loadThread = useCallback((uid) => {
    api.get(`/chat/thread/${uid}`, { silent: true }).then((r) => {
      setMsgs(r.data || []);
      setTimeout(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, 50);
    }).catch(() => {});
  }, []);

  useEffect(() => { poll(); const iv = setInterval(poll, 7000); return () => clearInterval(iv); }, [poll]);
  useEffect(() => {
    if (!active || mini) return;
    loadThread(active.id);
    const iv = setInterval(() => loadThread(active.id), 4000);
    return () => clearInterval(iv);
  }, [active, mini, loadThread]);

  const send = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setText("");
    try {
      const r = await api.post("/chat/send", { to_id: active.id, text: t }, { silent: true });
      setMsgs((m) => [...m, r.data]);
      setTimeout(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, 50);
    } catch (e) {}
  };

  if (!user || disabled) return null;

  return (
    <div ref={containerRef} data-testid="chat-dock" className="fixed bottom-3 right-3 z-[60] select-none">
      {!open && (
        <button data-testid="chat-launcher"
          onClick={() => { setOpen(true); poll(); }}
          title="MESAJLARI AÇ"
          className="relative flex items-center gap-1.5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-lg hover:brightness-110">
          <MessageCircle className="h-4 w-4" /> MESAJLAR
          <span data-testid="chat-online-count" className="ml-1 flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px]"><span className="h-2 w-2 rounded-full bg-emerald-400" />{onlineCount} ONLINE</span>
          {totalUnread > 0 && <span data-testid="chat-unread-badge" className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold">{totalUnread}</span>}
        </button>
      )}

      {open && (
        <div data-testid="chat-window" className={`flex ${mini ? "h-auto" : "h-[480px]"} w-[340px] flex-col overflow-hidden rounded-xl border border-slate-700 bg-slate-900 shadow-2xl`}>
          <div className="flex items-center justify-between border-b border-slate-800 bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-2 text-white">
            <div className="flex items-center gap-2 text-sm font-bold">
              {active ? (
                <button data-testid="chat-back" onClick={() => { setActive(null); poll(); }} className="rounded p-1 hover:bg-white/20"><ArrowLeft className="h-4 w-4" /></button>
              ) : <MessageCircle className="h-4 w-4" />}
              {active ? active.name : `MESAJLAR · ${onlineCount} ONLINE`}
              {mini && totalUnread > 0 && <span className="rounded-full bg-rose-500 px-1.5 text-[10px]">{totalUnread}</span>}
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="chat-minimize" onClick={() => setMini((m) => !m)} className="rounded p-1 hover:bg-white/20" title={mini ? "BÜYÜT" : "DARALT"}>{mini ? <ChevronUp className="h-4 w-4" /> : <Minus className="h-4 w-4" />}</button>
              <button data-testid="chat-close" onClick={() => { setOpen(false); setMini(false); setActive(null); }} className="rounded p-1 hover:bg-white/20" title="KAPAT"><X className="h-4 w-4" /></button>
            </div>
          </div>

          {!mini && !active && (
            <div className="flex-1 overflow-y-auto">
              {users.length === 0 && <div className="px-4 py-10 text-center text-xs text-slate-500">BAŞKA KULLANICI YOK</div>}
              {users.map((u, i) => (
                <React.Fragment key={u.id}>
                {u.role !== "super_admin" && (i === 0 || users[i - 1].role === "super_admin" || users[i - 1].online !== u.online) && <div className="bg-slate-800/70 px-3 py-1 text-[10px] font-bold text-slate-400">{u.online ? "ONLINE" : "ÇEVRİMDIŞI"}</div>}
                <button data-testid={`chat-user-${u.id}`} onClick={() => setActive({ id: u.id, name: u.name })}
                  className="flex w-full items-center gap-3 border-b border-slate-800/60 px-3 py-2.5 text-left hover:bg-slate-800/60">
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${u.online ? "bg-emerald-400" : "bg-slate-600"}`} />
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm text-slate-200">{u.name}</span>{u.role === "super_admin" ? <span data-testid="chat-maker-admin" className="block truncate text-[10px] font-bold text-amber-300"><span className="tracking-tighter text-amber-400">★★★★★</span> YAPIMCI ADMIN</span> : u.firm && <span className="block truncate text-[10px] text-slate-500">{u.firm}</span>}</span>
                  {u.unread > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{u.unread}</span>}
                </button>
                </React.Fragment>
              ))}
            </div>
          )}

          {!mini && active && (
            <>
              <div ref={bodyRef} className="flex-1 space-y-2 overflow-y-auto bg-slate-950/40 p-3">
                {msgs.length === 0 && <div className="py-6 text-center text-xs text-slate-500">HENÜZ MESAJ YOK</div>}
                {msgs.map((m) => {
                  const mine = m.from_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 text-sm ${mine ? "rounded-br-sm bg-blue-600 text-white" : "rounded-bl-sm bg-slate-800 text-slate-100"}`}>
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        <div className={`mt-0.5 text-[9px] ${mine ? "text-blue-200" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                          {mine && (m.read ? <span data-testid={`msg-read-${m.id}`} className="ml-1 inline-flex items-center gap-0.5 font-bold text-emerald-300"><CheckCheck className="h-3 w-3" />OKUNDU</span>
                            : m.delivered_at ? <span data-testid={`msg-seen-${m.id}`} className="ml-1 inline-flex items-center gap-0.5"><CheckCheck className="h-3 w-3" />GÖRÜLDÜ</span>
                            : <span className="ml-1 inline-flex items-center"><Check className="h-3 w-3" /></span>)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 border-t border-slate-800 p-2">
                <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="MESAJ YAZ..." className="h-9 flex-1 rounded-full border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none" />
                <button data-testid="chat-send" onClick={send} className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700"><Send className="h-4 w-4" /></button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
